import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {useSearchParams} from 'react-router-dom';

import {broker} from "../../../config/broker.ts";
import {useSignInProfiles} from "../../sign-in-profiles/hooks/useSignInProfiles.ts";
import {parseHandoffQuery} from "../services/HandoffQueryParser.ts";
import {returnToBroker} from "../services/BrokerReturnService.ts";
import {forwardToBrokerFederated} from "../services/FederatedForwardService.ts";
import {resolveAccountForEmail} from "../services/PoolResolver.ts";
import {
    resumeAnyPlatformSession,
    signInErrorMessage,
    signInWithCredentials
} from "../services/HandoffSignInService.ts";
import {
    readRememberedAccount,
    saveRememberedAccount
} from "../services/PlatformSessionStore.ts";
import {HANDOFF_MESSAGES} from "../config/messages.ts";
import {
    FederatedTarget,
    HandoffClient,
    HandoffCredentials,
    HandoffEmail
} from "../types.ts";

export type HandoffState =
    | { phase: 'error', message: string }
    | { phase: 'checking' }
    | { phase: 'email', email: string, signInError: string | null }
    | { phase: 'password', email: string, client: HandoffClient, signInError: string | null }
    | { phase: 'federated', target: FederatedTarget }
    | { phase: 'selection', target: FederatedTarget }
    | { phase: 'redirecting' };

export const useHandoff = () => {
    const [searchParams] = useSearchParams();
    const query = useMemo(() => parseHandoffQuery(searchParams), [searchParams]);
    const brokerBaseUrl = broker.baseUrl;

    const {
        activeProfile,
        handleFetchSignInProfiles,
    } = useSignInProfiles();

    const [state, setState] = useState<HandoffState>(() => {
        if (!query.ok) return {phase: 'error', message: query.error};
        if (!brokerBaseUrl) return {phase: 'error', message: HANDOFF_MESSAGES.unavailable};
        return {phase: 'checking'};
    });
    const started = useRef(false);
    const profilesRequested = useRef(false);

    const initialEmail = useMemo(() => {
        if (!query.ok) return '';
        const remembered = readRememberedAccount();
        if (!remembered) return '';
        return remembered.identityProvider || query.value.clients.some((c) => c.poolId === remembered.poolId) ? remembered.email : '';
    }, [query]);

    const federatedTarget = useCallback((email: string, poolId: string, identityProvider: string): FederatedTarget => ({
        email,
        poolId,
        identityProvider,
        passwordClient: query.ok ? query.value.clients.find((c) => c.poolId === poolId) ?? null : null,
    }), [query]);

    useEffect(() => {
        if (state.phase !== 'checking' || started.current || !query.ok) {
            return;
        }
        started.current = true;

        const {request, clients} = query.value;
        const afterNoSession = () => {
            // A remembered federated account is only a hint of which IdP to use; the broker checks it.
            const remembered = readRememberedAccount();
            if (remembered?.identityProvider && remembered.poolId && remembered.email) {
                setState({phase: 'federated', target: federatedTarget(remembered.email, remembered.poolId, remembered.identityProvider)});
                return;
            }
            setState({phase: 'email', email: initialEmail, signInError: null});
        };

        resumeAnyPlatformSession(clients)
            .then((tokens) => {
                if (tokens === null) {
                    afterNoSession();
                    return;
                }
                setState({phase: 'redirecting'});
                returnToBroker(brokerBaseUrl, request, tokens);
            })
            .catch(afterNoSession);
    }, [state.phase, query, brokerBaseUrl, initialEmail, federatedTarget]);

    const forwardFederated = useCallback((target: FederatedTarget) => {
        if (!query.ok) {
            return;
        }
        saveRememberedAccount({email: target.email, poolId: target.poolId, identityProvider: target.identityProvider});
        setState({phase: 'redirecting'});
        forwardToBrokerFederated(brokerBaseUrl, query.value.request, target.poolId, target.identityProvider);
    }, [query, brokerBaseUrl]);

    // Same rule as legacy method selection: passwordAndSso offers the choice, otherwise forward at once.
    useEffect(() => {
        if (state.phase !== 'federated') {
            return;
        }
        if (!profilesRequested.current) {
            profilesRequested.current = true;
            handleFetchSignInProfiles();
        }
        if (typeof activeProfile === 'undefined') {
            return;
        }
        if (activeProfile.passwordAndSso === true) {
            setState({phase: 'selection', target: state.target});
            return;
        }
        forwardFederated(state.target);
    }, [state, activeProfile, handleFetchSignInProfiles, forwardFederated]);

    const handleEmail = useCallback(async ({email}: HandoffEmail) => {
        if (!query.ok) {
            return;
        }
        setState({phase: 'email', email, signInError: null});
        try {
            const account = await resolveAccountForEmail(query.value.clients, email);
            if (account.kind === 'federated') {
                setState({phase: 'federated', target: federatedTarget(email, account.poolId, account.identityProvider)});
                return;
            }
            setState({phase: 'password', email, client: account.client, signInError: null});
        } catch (e) {
            setState({phase: 'email', email, signInError: signInErrorMessage(e)});
        }
    }, [query, federatedTarget]);

    const handleSignIn = useCallback(async (values: HandoffCredentials) => {
        if (!query.ok || state.phase !== 'password') {
            return;
        }
        const {client} = state;
        setState({...state, signInError: null});
        try {
            const tokens = await signInWithCredentials(client, values);
            setState({phase: 'redirecting'});
            returnToBroker(brokerBaseUrl, query.value.request, tokens);
        } catch (e) {
            setState({phase: 'password', email: values.email, client, signInError: signInErrorMessage(e)});
        }
    }, [query, state, brokerBaseUrl]);

    const handleSelectSso = useCallback(() => {
        if (state.phase === 'selection') {
            forwardFederated(state.target);
        }
    }, [state, forwardFederated]);

    const handleSelectPassword = useCallback(() => {
        if (state.phase === 'selection' && state.target.passwordClient) {
            setState({phase: 'password', email: state.target.email, client: state.target.passwordClient, signInError: null});
        }
    }, [state]);

    const handleUseAnotherAccount = useCallback(() => {
        setState({phase: 'email', email: '', signInError: null});
    }, []);

    return {
        state,
        handleEmail,
        handleSignIn,
        handleSelectSso,
        handleSelectPassword,
        handleUseAnotherAccount,
    };
};
